import React, { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Option, Select, Spinner } from '@material-tailwind/react';
import { DownloadIcon, AlertTriangleIcon, PackageXIcon, ClipboardCheckIcon } from 'lucide-react';
import moment from 'moment';
import { useGetInventoryItems, useGetInventoryConsumptionTrend, useGetInventoryConsumptionByFloor, useGetInventoryFloorItemBreakdown, useGetInventoryBreakageReport, useGetInventoryLowStockReport, useGetInventoryCountMismatchesReport } from '../../custom-hooks/use-inventory';
import { useGetFloors } from '../../custom-hooks/use-floors';
import { ExportInventoryItemCsv } from '../../services/backend';
import { CATEGORY_META, TRANSACTION_TYPE_META, downloadCsvResponse, formatQty } from './utils';
import { Notify, NotifyError } from '../../utils/functions';
import { INVENTORY_CATEGORIES_ENUM } from '../../utils/enums';

const tooltipStyle = { borderRadius: 12, border: '1px solid #E6DDCF', fontSize: 12, boxShadow: '0 12px 24px -12px rgba(40,22,8,0.25)' };

// Backend groups by day ("2026-07-11"), ISO week ("2026-W28"), or month ("2026-07")
// depending on granularity — each needs its own label parsing, only "daily" is a
// moment-parseable date string on its own.
const formatTrendLabel = (value, granularity, short) => {
    if (granularity === 'weekly') {
        const [year, week] = value.split('-W');
        return short ? `Wk ${week}` : `Week ${week}, ${year}`;
    }
    if (granularity === 'monthly') {
        return moment(value, 'YYYY-MM').format(short ? 'MMM' : 'MMMM YYYY');
    }
    return moment(value).format(short ? 'D MMM' : 'DD MMM YYYY');
};

const Card = ({ title, subtitle, action, children }) => (
    <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
                <h3 className="font-bold text-inv-ink">{title}</h3>
                {subtitle && <p className="text-xs text-[#8C8175]">{subtitle}</p>}
            </div>
            {action}
        </div>
        {children}
    </div>
);

const getFloorLabel = (f) => `Floor ${f.number}${f.building?.name ? ` — ${f.building.name}` : ''}`;

const ReportsView = () => {
    const [itemId, setItemId] = useState('');
    const [category, setCategory] = useState('');
    const [floorId, setFloorId] = useState('');
    const [days, setDays] = useState(30);
    const [granularity, setGranularity] = useState('daily');
    const [exporting, setExporting] = useState(false);

    // An item belongs to exactly one category, so letting both filters be set at once is
    // ambiguous (e.g. an item from "Cleaning" filtered against category "Beverages" would
    // just silently return nothing) — picking one clears the other instead.
    const handleItemChange = (v) => {
        setItemId(v);
        if (v) setCategory('');
    };
    const handleCategoryChange = (v) => {
        setCategory(v);
        if (v) setItemId('');
    };

    const { data: items = [] } = useGetInventoryItems();
    const { data: floors = [] } = useGetFloors('all');
    const { data: trend = [], isLoading: trendLoading } = useGetInventoryConsumptionTrend({ itemId: itemId || undefined, category: category || undefined, floor: floorId || undefined, days, granularity });
    const { data: byFloor = [], isLoading: byFloorLoading } = useGetInventoryConsumptionByFloor({ itemId: itemId || undefined, category: category || undefined, days });
    const { data: floorItemBreakdown = [], isLoading: floorItemBreakdownLoading } = useGetInventoryFloorItemBreakdown({ floor: floorId || undefined, category: category || undefined, days });
    const { data: breakageData, isLoading: breakageLoading } = useGetInventoryBreakageReport({ days });
    const { data: mismatches = [], isLoading: mismatchesLoading } = useGetInventoryCountMismatchesReport();
    const { data: lowStockData, isLoading: lowStockLoading } = useGetInventoryLowStockReport();

    const handleExport = async () => {
        if (!itemId) return;
        setExporting(true);
        try {
            const response = await ExportInventoryItemCsv(itemId);
            const item = items.find((i) => i._id === itemId);
            downloadCsvResponse(response, `${item?.name || 'item'}_log.csv`);
            Notify('Log exported successfully', 'success');
        } catch (error) {
            NotifyError('Failed to export log', error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-5">
            <Card
                title="Consumption Trend"
                subtitle="OUT + breakage over time"
                action={
                    <div className="flex flex-wrap gap-3">
                        {/* `selected` computes the closed-box label ourselves — Material Tailwind's
                            own display lookup for the closed Select is unreliable in this version */}
                        <div className="w-full shrink-0 sm:w-56"><Select
                            label="Item"
                            value={itemId}
                            onChange={handleItemChange}
                            selected={() => (itemId ? items.find((i) => i._id === itemId)?.name || '' : 'All Items')}
                        >
                            <Option value="">All Items</Option>
                            {items.map((i) => <Option key={i._id} value={i._id}>{i.name}</Option>)}
                        </Select></div>
                        <div className="w-full shrink-0 sm:w-56"><Select
                            label="Category"
                            value={category}
                            onChange={handleCategoryChange}
                            disabled={!!itemId}
                            selected={() => (itemId ? 'N/A (item selected)' : category ? CATEGORY_META[category]?.label || category : 'All Categories')}
                        >
                            <Option value="">All Categories</Option>
                            {INVENTORY_CATEGORIES_ENUM.map((cat) => <Option key={cat} value={cat}>{CATEGORY_META[cat]?.label || cat}</Option>)}
                        </Select></div>
                        <div className="w-full shrink-0 sm:w-56"><Select
                            label="Floor"
                            value={floorId}
                            onChange={setFloorId}
                            selected={() => {
                                if (!floorId) return 'All Floors';
                                const f = floors.find((fl) => fl._id === floorId);
                                return f ? getFloorLabel(f) : '';
                            }}
                        >
                            <Option value="">All Floors</Option>
                            {floors.map((f) => (
                                <Option key={f._id} value={f._id}>{getFloorLabel(f)}</Option>
                            ))}
                        </Select></div>
                        <div className="w-full shrink-0 sm:w-56"><Select
                            label="Range"
                            value={String(days)}
                            onChange={(v) => setDays(Number(v))}
                            selected={() => ({ 7: '7 days', 30: '30 days', 90: '90 days' }[days] || `${days} days`)}
                        >
                            <Option value="7">7 days</Option>
                            <Option value="30">30 days</Option>
                            <Option value="90">90 days</Option>
                        </Select></div>
                        <div className="w-full shrink-0 sm:w-56"><Select
                            label="View by"
                            value={granularity}
                            onChange={setGranularity}
                            selected={() => ({ daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }[granularity] || 'Daily')}
                        >
                            <Option value="daily">Daily</Option>
                            <Option value="weekly">Weekly</Option>
                            <Option value="monthly">Monthly</Option>
                        </Select></div>
                        {itemId && (
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="flex items-center gap-1.5 rounded-xl border border-inv-cream-200 px-3 py-2 text-sm font-semibold text-inv-ink hover:bg-inv-cream-100 disabled:opacity-50"
                            >
                                <DownloadIcon size={14} /> Export CSV
                            </button>
                        )}
                    </div>
                }
            >
                {trendLoading ? (
                    <div className="flex h-[260px] items-center justify-center"><Spinner className="h-6 w-6 text-inv-brand" /></div>
                ) : trend.length === 0 ? (
                    <div className="flex h-[260px] items-center justify-center text-sm text-[#8C8175]">No consumption in this range</div>
                ) : (
                    <div className="h-[260px] w-full">
                        <ResponsiveContainer width="100%" height="100%" debounce={200}>
                            <LineChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="#e1e0d9" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} tickFormatter={(d) => formatTrendLabel(d, granularity, true)} />
                                <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={tooltipStyle} labelFormatter={(d) => formatTrendLabel(d, granularity, false)} />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                                <Line type="monotone" dataKey="out" name={TRANSACTION_TYPE_META.out.label} stroke={TRANSACTION_TYPE_META.out.hex} strokeWidth={2} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="breakage" name={TRANSACTION_TYPE_META.breakage.label} stroke={TRANSACTION_TYPE_META.breakage.hex} strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            <Card
                title="Compare Floors"
                subtitle={itemId
                    ? `${items.find((i) => i._id === itemId)?.name || 'Item'} — which floor uses it most — last ${days} days`
                    : `Every floor, side by side — last ${days} days · pick an Item above to compare floors for just that item`}
            >
                {byFloorLoading ? (
                    <div className="flex h-[280px] items-center justify-center"><Spinner className="h-6 w-6 text-inv-brand" /></div>
                ) : byFloor.length === 0 ? (
                    <div className="flex h-[280px] items-center justify-center text-sm text-[#8C8175]">No floors found</div>
                ) : (
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%" debounce={200}>
                            <BarChart
                                data={byFloor.map((f) => ({ ...f, label: `Floor ${f.floorNumber}${f.buildingName ? ` — ${f.buildingName}` : ''}` }))}
                                margin={{ top: 8, right: 8, left: -20, bottom: 55 }}
                                barCategoryGap="30%"
                            >
                                <CartesianGrid vertical={false} stroke="#e1e0d9" />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11, fill: '#898781' }}
                                    axisLine={{ stroke: '#c3c2b7' }}
                                    tickLine={false}
                                    interval={0}
                                    angle={-25}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#F1E9DC' }} />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                                <Bar dataKey="out" name={TRANSACTION_TYPE_META.out.label} stackId="qty" fill={TRANSACTION_TYPE_META.out.hex} maxBarSize={48} />
                                <Bar dataKey="breakage" name={TRANSACTION_TYPE_META.breakage.label} stackId="qty" fill={TRANSACTION_TYPE_META.breakage.hex} radius={[4, 4, 0, 0]} maxBarSize={48} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            <Card
                title="Items on This Floor"
                subtitle={floorId
                    ? `${getFloorLabel(floors.find((f) => f._id === floorId) || { number: '' })} — which items it uses most — last ${days} days`
                    : 'Pick a Floor above to see which items it consumes'}
            >
                {!floorId ? (
                    <div className="flex h-[280px] items-center justify-center px-6 text-center text-sm text-[#8C8175]">
                        Select a floor in the filter above to see its item-by-item breakdown.
                    </div>
                ) : floorItemBreakdownLoading ? (
                    <div className="flex h-[280px] items-center justify-center"><Spinner className="h-6 w-6 text-inv-brand" /></div>
                ) : floorItemBreakdown.length === 0 ? (
                    <div className="flex h-[280px] items-center justify-center text-sm text-[#8C8175]">No consumption recorded for this floor in this range</div>
                ) : (() => {
                    // Only widen + scroll once there are actually enough items to need it —
                    // otherwise a handful of bars end up stranded on the left of an
                    // artificially wide chart with empty space to the right.
                    const manyFloorItems = floorItemBreakdown.length > 8;
                    const chart = (
                        <BarChart data={floorItemBreakdown} margin={{ top: 8, right: 8, left: -20, bottom: 65 }} barCategoryGap="30%">
                            <CartesianGrid vertical={false} stroke="#e1e0d9" />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 11, fill: '#898781' }}
                                axisLine={{ stroke: '#c3c2b7' }}
                                tickLine={false}
                                interval={0}
                                angle={-30}
                                textAnchor="end"
                                height={70}
                            />
                            <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#F1E9DC' }} />
                            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                            <Bar dataKey="out" name={TRANSACTION_TYPE_META.out.label} stackId="qty" fill={TRANSACTION_TYPE_META.out.hex} maxBarSize={40} />
                            <Bar dataKey="breakage" name={TRANSACTION_TYPE_META.breakage.label} stackId="qty" fill={TRANSACTION_TYPE_META.breakage.hex} radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                    );
                    return manyFloorItems ? (
                        <div className="overflow-x-auto">
                            <div style={{ width: floorItemBreakdown.length * 64, height: 320 }}>
                                <ResponsiveContainer width="100%" height="100%" debounce={200}>{chart}</ResponsiveContainer>
                            </div>
                        </div>
                    ) : (
                        <div className="h-[320px] w-full">
                            <ResponsiveContainer width="100%" height="100%" debounce={200}>{chart}</ResponsiveContainer>
                        </div>
                    );
                })()}
            </Card>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <Card title="Low Stock & Daily Max" subtitle="Items needing attention">
                    {lowStockLoading ? (
                        <div className="flex justify-center py-8"><Spinner className="h-5 w-5 text-inv-brand" /></div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-600">
                                    <AlertTriangleIcon size={13} /> Low Stock ({lowStockData?.lowStock?.length || 0})
                                </div>
                                {lowStockData?.lowStock?.length ? (
                                    <ul className="space-y-1.5">
                                        {lowStockData.lowStock.map((item) => (
                                            <li key={item._id} className="flex justify-between rounded-lg bg-red-50 px-3 py-2 text-sm">
                                                <span className="font-medium text-inv-ink">{item.name}</span>
                                                <span className="text-red-600">{item.currentRemaining} / {formatQty(item.reorderLevel, item.unitOfMeasure)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p className="text-sm text-[#8C8175]">Nothing low on stock.</p>}
                            </div>
                            <div>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8C8175]">Recent Daily-Max Breaches</div>
                                {lowStockData?.overDailyMax?.length ? (
                                    <ul className="space-y-1.5">
                                        {lowStockData.overDailyMax.slice(0, 5).map((t) => (
                                            <li key={t._id} className="flex justify-between rounded-lg bg-inv-cream-100 px-3 py-2 text-sm">
                                                <span className="font-medium text-inv-ink">{t.item?.name}</span>
                                                <span className="text-[#6B675F]">{moment(t.occurredAt).format('DD MMM')}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p className="text-sm text-[#8C8175]">No recent breaches.</p>}
                            </div>
                        </div>
                    )}
                </Card>

                <Card title="Breakage Report" subtitle={`Last ${days} days`}>
                    {breakageLoading ? (
                        <div className="flex justify-center py-8"><Spinner className="h-5 w-5 text-inv-brand" /></div>
                    ) : breakageData?.summary?.length ? (
                        <ul className="space-y-1.5">
                            {breakageData.summary.map((s) => (
                                <li key={s.item?._id} className="flex items-center justify-between rounded-lg bg-inv-cream-100 px-3 py-2 text-sm">
                                    <span className="flex items-center gap-1.5 font-medium text-inv-ink"><PackageXIcon size={13} className="text-red-500" /> {s.item?.name}</span>
                                    <span className="text-[#6B675F]">{formatQty(s.totalBroken, s.item?.unitOfMeasure)} broken</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-[#8C8175]">No breakage recorded in this range.</p>
                    )}
                </Card>

                <Card title="Count Mismatches" subtitle="Physical counts that didn't match system stock">
                    {mismatchesLoading ? (
                        <div className="flex justify-center py-8"><Spinner className="h-5 w-5 text-inv-brand" /></div>
                    ) : mismatches.length ? (
                        <ul className="space-y-1.5">
                            {mismatches.slice(0, 8).map((m) => (
                                <li key={m._id} className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2 text-sm">
                                    <span className="flex items-center gap-1.5 font-medium text-inv-ink">
                                        <ClipboardCheckIcon size={13} className="text-red-500" /> {m.item?.name}
                                    </span>
                                    <span className="text-red-600">{formatQty(`${m.variance > 0 ? '+' : ''}${m.variance}`, m.item?.unitOfMeasure)}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-[#8C8175]">No count mismatches recorded.</p>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ReportsView;
